import { useMemo } from "react"
import { socialMediaShareMap } from "QuorumGrassroots/helperFunctions"
import { SocialPlatform } from "QuorumGrassroots/widgets/GotvCampaignFooter/GotvCampaignFooter"

export interface UseGotvSocialSharingParams {
    /** Array of social media platform values from campaign */
    socialMediaPlatforms?: number[]
    /** URL to share */
    shareUrl?: string
    /** Social media message/text */
    shareText?: string
    /** Image URL for social media */
    imageUrl?: string
    /** Organization logo (fallback for image) */
    organizationLogo?: string
}

/**
 * Hook to generate social sharing button data for GOTV campaigns
 * Based on the campaign's social media configuration
 */
export const useGotvSocialSharing = ({
    socialMediaPlatforms = [],
    shareUrl = "",
    shareText = "",
    imageUrl = "",
    organizationLogo = "",
}: UseGotvSocialSharingParams): SocialPlatform[] => {
    return useMemo(() => {
        if (!socialMediaPlatforms || socialMediaPlatforms.length === 0) {
            return []
        }

        const media = imageUrl || organizationLogo

        return socialMediaPlatforms
            .filter((platformType) => {
                return platformType !== DjangIO.app.grassroots.enums.SocialMediaType.pinterest.value
            })
            .map((platformType): SocialPlatform | null => {
                const platform = DjangIO.app.grassroots.enums.SocialMediaType.by_value(platformType)
                const shareFunction = socialMediaShareMap[platformType]

                if (!platform || !shareFunction) {
                    return null
                }

                return {
                    platform: platformType,
                    icon: platform.icon,
                    onClick: () => shareFunction({ url: shareUrl, media, text: shareText }),
                    dataCy: `social-share-${platform.label.toLowerCase().replace(/\s+/g, "-")}`,
                }
            })
            .filter((item): item is SocialPlatform => item !== null)
    }, [socialMediaPlatforms, shareUrl, shareText, imageUrl, organizationLogo])
}
